import { RE_SC_LINK } from '../config';

/**
 * SpaceChat Deep Link Parser
 *
 * Parses sc:// and spacechat:// protocol URLs into typed structures.
 *
 * Supported URL formats:
 *   sc://chat/{id}              - Open chat by numeric ID
 *   sc://chat/{username}        - Open chat by username
 *   sc://user/{id}              - Open user profile by ID
 *   sc://user/{username}        - Open user profile by username
 *   sc://channel/{id}           - Open channel by ID
 *   sc://group/{id}             - Open group by ID
 *   sc://invite/{hash}          - Join via invite link
 *   sc://message/{chatId}/{msgId} - Open specific message
 */

// Link type discriminants
export type SpaceChatDeepLinkType =
  | 'chatById'
  | 'chatByUsername'
  | 'userById'
  | 'userByUsername'
  | 'channel'
  | 'group'
  | 'invite'
  | 'message';

// Base interface
interface BaseSpaceChatDeepLink {
  type: SpaceChatDeepLinkType;
}

// Specific link types
export interface ChatByIdLink extends BaseSpaceChatDeepLink {
  type: 'chatById';
  chatId: string;
}

export interface ChatByUsernameLink extends BaseSpaceChatDeepLink {
  type: 'chatByUsername';
  username: string;
}

export interface UserByIdLink extends BaseSpaceChatDeepLink {
  type: 'userById';
  userId: string;
}

export interface UserByUsernameLink extends BaseSpaceChatDeepLink {
  type: 'userByUsername';
  username: string;
}

export interface ChannelLink extends BaseSpaceChatDeepLink {
  type: 'channel';
  channelId: string;
}

export interface GroupLink extends BaseSpaceChatDeepLink {
  type: 'group';
  groupId: string;
}

export interface InviteLink extends BaseSpaceChatDeepLink {
  type: 'invite';
  hash: string;
}

export interface MessageLink extends BaseSpaceChatDeepLink {
  type: 'message';
  chatId: string;
  messageId: number;
  threadId?: number;
}

// Union type for all parsed links
export type ParsedSpaceChatDeepLink =
  | ChatByIdLink
  | ChatByUsernameLink
  | UserByIdLink
  | UserByUsernameLink
  | ChannelLink
  | GroupLink
  | InviteLink
  | MessageLink;

/**
 * Check if a value is a numeric ID (positive or negative integer)
 */
function isNumericId(value: string): boolean {
  return /^-?\d+$/.test(value);
}

/**
 * Check if a URL is a SpaceChat deep link (sc:// or spacechat://)
 */
export function isSpaceChatDeepLink(url: string): boolean {
  return RE_SC_LINK.test(url);
}

/**
 * Try to parse a SpaceChat deep link URL
 * Returns undefined if the URL is not a valid SpaceChat deep link
 */
export function tryParseSpaceChatDeepLink(url: string): ParsedSpaceChatDeepLink | undefined {
  if (!isSpaceChatDeepLink(url)) {
    return undefined;
  }

  try {
    return parseSpaceChatDeepLink(url);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Failed to parse SpaceChat deep link:', url, err);
    return undefined;
  }
}

/**
 * Parse a SpaceChat deep link URL into a typed structure
 */
function parseSpaceChatDeepLink(url: string): ParsedSpaceChatDeepLink | undefined {
  // Normalize URL: convert sc:// or spacechat:// to http:// for URL parsing
  // This is needed because URL class doesn't handle custom protocols well
  const normalizedUrl = url.replace(/^(sc|spacechat):\/\//i, 'http://sc.local/');

  const parsed = new URL(normalizedUrl);
  const pathParts = parsed.pathname.split('/').filter(Boolean);
  const queryParams = Object.fromEntries(parsed.searchParams);

  if (pathParts.length === 0) {
    return undefined;
  }

  const [type, idOrUsername, thirdParam] = pathParts;

  switch (type.toLowerCase()) {
    case 'chat': {
      if (!idOrUsername) return undefined;
      return isNumericId(idOrUsername)
        ? { type: 'chatById', chatId: idOrUsername }
        : { type: 'chatByUsername', username: idOrUsername };
    }

    case 'user': {
      if (!idOrUsername) return undefined;
      return isNumericId(idOrUsername)
        ? { type: 'userById', userId: idOrUsername }
        : { type: 'userByUsername', username: idOrUsername };
    }

    case 'channel': {
      if (!idOrUsername) return undefined;
      return { type: 'channel', channelId: idOrUsername };
    }

    case 'group': {
      if (!idOrUsername) return undefined;
      return { type: 'group', groupId: idOrUsername };
    }

    case 'invite': {
      if (!idOrUsername) return undefined;
      return { type: 'invite', hash: idOrUsername };
    }

    case 'message': {
      if (!idOrUsername || !thirdParam) return undefined;
      const messageId = Number(thirdParam);
      if (Number.isNaN(messageId)) return undefined;

      const threadId = queryParams.thread ? Number(queryParams.thread) : undefined;

      return {
        type: 'message',
        chatId: idOrUsername,
        messageId,
        threadId: threadId && !Number.isNaN(threadId) ? threadId : undefined,
      };
    }

    default:
      return undefined;
  }
}
